import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Wallet,
  BadgeCheck,
  Star,
  Search,
  CheckCircle2,
  CreditCard,
  MapPin,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Logo, TrustBadge, formatRupiah } from "../components/ui";
import { api } from "../lib/api";

const CATEGORIES = [
  { name: "Kebersihan Rumah", desc: "Bersih-bersih & cuci", img: "/img/cleaning.jpg" },
  { name: "Servis AC", desc: "Pasang & perbaikan AC", img: "/img/ac.jpg" },
  { name: "Tukang Ledeng", desc: "Pipa & keran bocor", img: "/img/plumber.jpg" },
  { name: "Listrik", desc: "Instalasi & perbaikan", img: "/img/electric.jpg" },
];

const STEPS = [
  { icon: Search, title: "Pilih layanan", desc: "Tentukan kategori jasa yang Anda butuhkan." },
  { icon: Star, title: "Pilih pekerja", desc: "Lihat rating, verifikasi, dan jumlah pekerjaan." },
  { icon: CheckCircle2, title: "Konfirmasi pesanan", desc: "Setujui detail dan jadwal dengan mudah." },
  { icon: CreditCard, title: "Bayar aman", desc: "Dana ditahan aman sampai pekerjaan selesai." },
  { icon: MapPin, title: "Pantau progres", desc: "Ikuti status pekerjaan sampai tuntas." },
];

export default function Landing() {
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/api/public/pricing").then((r) => setPricing(r.data)).catch(() => { });
  }, []);

  return (
    <div className="min-h-screen h-full bg-[#f6f8fc]">
      {/* Nav */}
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:container lg:mx-auto lg:h-20">
          <Logo light />
          <div className="flex items-center gap-2">
            <Link to="/login" className="btn-ghost !bg-transparent !text-white !border-white/30 hover:!border-white !py-2 !px-4 text-sm sm:!py-2.5 sm:!px-6" data-testid="nav-login-button">
              Masuk
            </Link>
            <Link to="/register" className="btn bg-white text-brand-700 hover:bg-brand-50 !py-2 !px-4 text-sm sm:!py-2.5 sm:!px-6" data-testid="nav-register-button">
              Daftar
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-brand-700 text-white overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-500/40 blur-3xl" />
        <div className="absolute right-1/4 bottom-0 h-72 w-72 rounded-full bg-success-500/20 blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 pt-24 pb-12 sm:pt-32 sm:pb-20 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center relative">
          <div className="animate-fade-up">
            <span className="chip bg-white/10 text-white mb-4 sm:mb-5">
              <Sparkles className="h-4 w-4 text-success-400" /> Jasa terpercaya se-Indonesia
            </span>
            <h1 className="leading-tight text-3xl sm:text-4xl md:text-5xl">
              Cari jasa profesional,
              <br />
              <span className="text-success-400">bayar dengan aman.</span>
            </h1>
            <p className="mt-4 sm:mt-5 text-base sm:text-lg md:text-xl text-brand-100 max-w-xl">
              Pesan tukang & pekerja terverifikasi semudah pesan makanan. Uang Anda ditahan aman
              (escrow) sampai pekerjaan benar-benar selesai.
            </p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="btn-success btn-lg w-full sm:w-auto" data-testid="hero-cta-register">
                Mulai Sekarang <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/register?role=WORKER" className="btn bg-white/10 text-white btn-lg hover:bg-white/20 w-full sm:w-auto" data-testid="hero-cta-worker">
                Gabung Jadi Pekerja
              </Link>
            </div>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-x-6 sm:gap-y-3">
              <TrustBadgeLight icon={ShieldCheck}>Pembayaran Aman</TrustBadgeLight>
              <TrustBadgeLight icon={BadgeCheck}>Pekerja Terverifikasi</TrustBadgeLight>
              <TrustBadgeLight icon={Wallet}>Dana Ditahan (Escrow)</TrustBadgeLight>
            </div>
          </div>

          <div className="mt-10 lg:mt-0 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="card overflow-hidden max-w-sm mx-auto lg:max-w-none">
              <img
                src="/img/hero.jpg"
                alt="Pekerja profesional Cariinjasa"
                className="h-48 sm:h-56 md:h-72 w-full object-cover"
                onError={(e) => { e.currentTarget.src = CATEGORIES[0].img; }}
              />
              <div className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-brand-100 grid place-items-center font-bold text-brand-700 text-sm">AS</div>
                    <div>
                      <p className="font-bold text-brand-800 text-sm sm:text-base">Agus Setiawan</p>
                      <p className="text-xs sm:text-sm text-brand-400 flex items-center gap-1">
                        <BadgeCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-success-500" /> Terverifikasi • Tier PRO
                      </p>
                    </div>
                  </div>
                  <span className="chip bg-success-50 text-success-600 text-xs sm:text-sm">
                    <Star className="h-3.5 w-3.5 sm:h-4 sm:w-4 fill-success-500 text-success-500" /> 4.9
                  </span>
                </div>
                <div className="mt-3 sm:mt-4 flex items-center justify-between rounded-2xl bg-brand-50 px-3 sm:px-4 py-2.5 sm:py-3">
                  <span className="text-brand-600 font-semibold text-sm">Pembayaran ditahan aman</span>
                  <span className="text-success-600 font-bold text-sm sm:text-base">{formatRupiah(350000)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid place-items-center">
        <div className="container mx-auto px-4 sm:px-6">

          {/* Categories */}
          <section className="py-12 sm:py-16">
            <h2 className="text-brand-800 text-2xl sm:text-3xl">Kategori layanan populer</h2>
            <p className="text-brand-400 mt-2 text-base sm:text-lg">Pilih kategori, kami temukan pekerja terbaik untuk Anda.</p>
            <div className="mt-6 sm:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {CATEGORIES.map((c, i) => (
                <div key={c.name} className="card overflow-hidden group animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`category-card-${i}`}>
                  <div className="h-28 sm:h-32 md:h-40 overflow-hidden">
                    <img src={c.img} alt={c.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-3 sm:p-4">
                    <p className="font-bold text-brand-800 text-sm sm:text-base">{c.name}</p>
                    <p className="text-xs sm:text-sm text-brand-400 mt-0.5">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How it works */}
          <section className="bg-white border-y border-brand-50">
            <div className="py-12 sm:py-16">
              <h2 className="text-brand-800 text-2xl sm:text-3xl text-center">Semudah 5 langkah</h2>
              <div className="mt-8 sm:mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
                {STEPS.map((s, i) => (
                  <div key={s.title} className="text-center">
                    <div className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-brand-50 grid place-items-center relative">
                      <s.icon className="h-7 w-7 sm:h-8 sm:w-8 text-brand-600" />
                      <span className="absolute -top-2 -right-2 h-5 w-5 sm:h-6 sm:w-6 rounded-full bg-success-500 text-white text-xs sm:text-sm font-bold grid place-items-center">
                        {i + 1}
                      </span>
                    </div>
                    <p className="mt-3 sm:mt-4 font-bold text-brand-800 text-sm sm:text-base">{s.title}</p>
                    <p className="text-xs sm:text-sm text-brand-400 mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Pricing transparency */}
          <section className="py-12 sm:py-16">
            <div className="card p-6 sm:p-8 md:p-10">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-6 w-6 sm:h-7 sm:w-7 text-success-500" />
                <h2 className="text-brand-800 text-xl sm:text-2xl md:text-3xl">Biaya transparan, tanpa kejutan</h2>
              </div>
              <div className="mt-6 sm:mt-8 grid sm:grid-cols-3 gap-4 sm:gap-6">
                <PriceCard
                  title="Biaya layanan pelanggan"
                  value={pricing?.client_fee ? `${pricing.client_fee.pct}%` : "6%"}
                  note={pricing?.client_fee ? `Maks ${formatRupiah(pricing.client_fee.cap)}` : "Maks Rp 25.000"}
                />
                <PriceCard
                  title="Masa perlindungan"
                  value={pricing?.protection_window_days ? `${pricing.protection_window_days} hari` : "7 hari"}
                  note="Garansi setelah pekerjaan selesai"
                />
                <PriceCard
                  title="Konfirmasi otomatis"
                  value={pricing?.auto_approval_hours ? `${pricing.auto_approval_hours} jam` : "72 jam"}
                  note="Jika pelanggan tidak merespon"
                />
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="pb-12 sm:pb-16 lg:pb-20">
            <div className="rounded-3xl bg-brand-700 text-white p-8 sm:p-10 md:p-14 text-center relative overflow-hidden">
              <div className="absolute -left-16 -bottom-16 h-72 w-72 rounded-full bg-success-500/20 blur-3xl" />
              <h2 className="relative text-2xl sm:text-3xl">Siap mulai? Gratis untuk mendaftar.</h2>
              <p className="relative mt-3 text-brand-100 text-base sm:text-lg">Bergabung dengan ribuan pelanggan dan pekerja terpercaya.</p>
              <div className="relative mt-6 sm:mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <Link to="/register" className="btn-success btn-lg w-full sm:w-auto" data-testid="cta-register-bottom">Daftar Sekarang</Link>
                <Link to="/login" className="btn bg-white/10 text-white btn-lg hover:bg-white/20 w-full sm:w-auto" data-testid="cta-login-bottom">Masuk</Link>
              </div>
            </div>
          </section>

        </div>
      </div>

      <footer className="border-t border-brand-50 bg-white">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 grid grid-cols-1 lg:grid-cols-3 place-items-center gap-4">
          <Logo />
          <p className="text-xs sm:text-sm text-brand-400 text-center">
            Powered by <a className="font-bold" href="https://websitejoki.my.id">WebsitejokiID</a> Engine
          </p>
          <p className="text-xs sm:text-sm text-brand-400 text-center md:text-right">© 2026 Cariinjasa.com — Jasa terpercaya, pembayaran aman.</p>
        </div>
      </footer>
    </div>
  );
}

function TrustBadgeLight({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-success-400" />
      <span className="font-semibold text-white/90 text-sm sm:text-base">{children}</span>
    </div>
  );
}

function PriceCard({ title, value, note }) {
  return (
    <div className="rounded-2xl border-2 border-brand-50 p-4 sm:p-6">
      <p className="text-brand-500 font-semibold text-sm sm:text-base">{title}</p>
      <p className="text-2xl sm:text-3xl font-extrabold text-brand-800 mt-2">{value}</p>
      <p className="text-xs sm:text-sm text-brand-400 mt-1">{note}</p>
    </div>
  );
}
